import React, { useEffect, useState } from 'react';
import { Col, Row, Radio } from 'antd';
import { isNil } from 'lodash';
import { RadioChangeEvent } from 'antd/lib/radio/interface';

import { AssignmentMultiRateViewModel } from 'api/backofficeApiClient';
import { formatNumberAsCurrency } from 'utils/numberUtils';
import { AssignmentDetailsModel } from 'utils/assignmentUtils';
import { NOT_AVAILABLE_PAY_CODE } from 'constants/MultiRateConstants';

import styles from './MultipleRatesDetails.module.less';

interface ExtendedAssignmentMultiRateViewModel
  extends AssignmentMultiRateViewModel {
  payCodeName?: string;
}

export interface PayTypeInfoViewModel {
  payRateTypeId?: string;
  payRateTypeName?: string;
  payCodesInfo?: ExtendedAssignmentMultiRateViewModel[];
}

type Props = {
  multiRateInfo: PayTypeInfoViewModel;
  assignmentDetails: AssignmentDetailsModel;
};

export default function MultipleRatesDetails(props: Props) {
  const { multiRateInfo, assignmentDetails } = props;
  const payCodesInfo = multiRateInfo.payCodesInfo || [];
  const [activePayCode, setActivePayCode] = useState(payCodesInfo[0].payCodeId);
  const availablePayCodes = payCodesInfo.filter(
    (payCode) => payCode.payCodeName !== NOT_AVAILABLE_PAY_CODE,
  );

  const handlePayCodeChange = (event: RadioChangeEvent) => {
    setActivePayCode(event.target.value);
  };

  useEffect(() => {
    setActivePayCode(payCodesInfo[0].payCodeId);
    // eslint-disable-next-line
  }, [assignmentDetails]);

  return (
    <Row gutter={[8, 8]} className="titled-sheet">
      {availablePayCodes.length > 0 && (
        <Col xs={24}>
          <div className={styles.group}>
            <Radio.Group
              value={activePayCode}
              className="radio-tab-view"
              onChange={handlePayCodeChange}
            >
              {payCodesInfo.map((payCode) => (
                <Radio.Button key={payCode.payCodeId} value={payCode.payCodeId}>
                  {payCode.payCodeName}
                </Radio.Button>
              ))}
            </Radio.Group>
          </div>
        </Col>
      )}

      {payCodesInfo?.map((payCodeInfo) => {
        if (payCodeInfo.payCodeId !== activePayCode) return null;

        return (
          <React.Fragment key={payCodeInfo.id}>
            {!isNil(payCodeInfo.payRate) && (
              <Col xs={24} md={12}>
                <div className="title">Pay rate:</div>
                <div>
                  {formatNumberAsCurrency({
                    num: payCodeInfo?.payRate,
                  })}
                </div>
              </Col>
            )}
            {!isNil(payCodeInfo.billRate) && (
              <Col xs={24} md={12}>
                <div className="title">Bill rate:</div>
                <div>
                  {formatNumberAsCurrency({
                    num: payCodeInfo?.billRate,
                  })}
                </div>
              </Col>
            )}
            {!isNil(payCodeInfo.specialInstructions) && (
              <Col xs={24} md={12}>
                <div className="title">Special instructions:</div>
                <div>{payCodeInfo.specialInstructions}</div>
              </Col>
            )}
            {!isNil(payCodeInfo?.memberBilledIndicator) && (
              <Col xs={24} md={12} data-id="assignmentMemberBilledIndicator">
                <div className="title">Paid by:</div>
                <div>
                  {payCodeInfo.memberBilledIndicator ? 'Member' : 'Customer'}
                </div>
              </Col>
            )}
          </React.Fragment>
        );
      })}
    </Row>
  );
}
